import { isEqual } from "lodash";
import { Content } from "src/pages/Channels/ChannelDetail/types";

// ─── Public Types ─────────────────────────────────────────────────────────────

export type DiffField = "ref" | "rules" | "props";

export interface FieldChange {
  field: DiffField;
  published: unknown;
  draft: unknown;
}

// A changed container-level setting: the channel's `props`, a zone's `props`,
// or a playlist's `props`. Shallow - one entry per top-level key that differs.
export interface PropChange {
  key: string;
  published: unknown;
  draft: unknown;
}

export type DiffEntry<T> =
  | { op: "equal"; item: T }
  | { op: "insert"; item: T }
  | { op: "delete"; item: T }
  | {
      op: "update";
      published: T;
      draft: T;
      changes: FieldChange[];
      moved: boolean;
      fromIndex: number;
      toIndex: number;
    }
  | { op: "move"; item: T; fromIndex: number; toIndex: number };

export interface ZoneDiff<T extends ItemLike = ItemLike> {
  entries: DiffEntry<T>[];
  // Container-level settings diff: a playlist's own `props`, or a zone's `props`.
  propsChanges: PropChange[];
  // True when the list was too large to run LCS reorder detection (GRWBKK-1654).
  // Inserts/deletes/updates are still exact; move detection is skipped, so every
  // surviving item reports moved:false and no "move" entries are emitted.
  reorderDetectionSkipped: boolean;
}

export interface ChannelDiff {
  layoutChanged: boolean;
  newZones: string[];
  deletedZones: string[];
  // Channel-level settings diff (the top-level `content.props`).
  propsChanges: PropChange[];
  zones: Record<string, ZoneDiff>;
}

// ─── Shared Item Shape ────────────────────────────────────────────────────────

export interface ItemLike {
  list_id: string;
  content: {
    _ref?: { id: string; type: string };
    props?: unknown;
  };
  rules?: unknown[];
}

// ─── Internal Functions ───────────────────────────────────────────────────────

// Guard for the LCS reorder-detection step (GRWBKK-1654). The dp table costs
// O(n*m) time and memory in the number of items common to both lists; measured
// on Node 24: 2000×2000 ≈ 80ms / 32MB, 5000×5000 ≈ 280ms / 80MB, 8000×8000
// ≈ 730ms / 300MB+. Everything else in the diff is linear, so item count (not
// payload size) is the only freeze risk. Cap at ~2000×2000 cells; above it we
// skip reorder detection instead of freezing the page.
export const MAX_LCS_CELLS = 4_000_000;

// Returns the set of list_ids whose relative order is preserved in both sequences.
// When there is a tie (equal LCS length either way), items earlier in `pub` are
// preferred as stable — so the item that jumped forward is flagged as the mover.
function computeLCS(pub: string[], draft: string[]): Set<string> {
  const n = pub.length;
  const m = draft.length;
  const dp: number[][] = Array.from({ length: n + 1 }, () =>
    new Array(m + 1).fill(0),
  );
  // 1. Build the LCS length table
  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      if (pub[i - 1] === draft[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }
  const stable = new Set<string>();
  let i = n;
  let j = m;
  // 2. Backtrack from (n,m) to recover which ids stayed in order
  while (i > 0 && j > 0) {
    if (pub[i - 1] === draft[j - 1]) {
      stable.add(pub[i - 1]);
      i--;
      j--;
    } else if (dp[i - 1][j] >= dp[i][j - 1]) {
      i--;
    } else {
      j--;
    }
  }
  return stable;
}

function diffItemFields(published: ItemLike, draft: ItemLike): FieldChange[] {
  const changes: FieldChange[] = [];

  const pubRef = published.content._ref;
  const draftRef = draft.content._ref;
  if (pubRef?.id !== draftRef?.id || pubRef?.type !== draftRef?.type) {
    changes.push({ field: "ref", published: pubRef, draft: draftRef });
  }

  if (!isEqual(published.rules, draft.rules)) {
    changes.push({
      field: "rules",
      published: published.rules,
      draft: draft.rules,
    });
  }

  if (!isEqual(published.content.props ?? {}, draft.content.props ?? {})) {
    changes.push({
      field: "props",
      published: published.content.props,
      draft: draft.content.props,
    });
  }

  return changes;
}

// Shallow settings diff for a container's `props` object (channel, zone, or
// playlist). Compares each top-level key with deep equality and records the
// ones that differ. Keys are sorted so the output order is stable.
function diffProps(published: unknown, draft: unknown): PropChange[] {
  const pub = (published ?? {}) as Record<string, unknown>;
  const drf = (draft ?? {}) as Record<string, unknown>;
  const keys = Array.from(
    new Set([...Object.keys(pub), ...Object.keys(drf)]),
  ).sort();
  const changes: PropChange[] = [];
  for (const key of keys) {
    if (!isEqual(pub[key], drf[key])) {
      changes.push({ key, published: pub[key], draft: drf[key] });
    }
  }
  return changes;
}

function diffList<T extends ItemLike>(
  publishedList: T[],
  draftList: T[],
): { entries: DiffEntry<T>[]; reorderDetectionSkipped: boolean } {
  // publishedMap carries the published-list index so we can interleave deletions.
  const publishedMap = new Map(
    publishedList.map((item, i) => [item.list_id, { item, idx: i }]),
  );
  const draftSet = new Set(draftList.map((item) => item.list_id));

  const commonPubIds = publishedList
    .filter((item) => draftSet.has(item.list_id))
    .map((item) => item.list_id);
  const commonDraftIds = draftList
    .filter((item) => publishedMap.has(item.list_id))
    .map((item) => item.list_id);
  // Fallback for very large content: treating every common item as stable skips
  // reorder detection but keeps inserts/deletes/updates exact, in linear time.
  const reorderDetectionSkipped =
    commonPubIds.length * commonDraftIds.length > MAX_LCS_CELLS;
  const stableSet = reorderDetectionSkipped
    ? new Set(commonPubIds)
    : computeLCS(commonPubIds, commonDraftIds);

  const entries: DiffEntry<T>[] = [];
  // Tracks the published index of the last stable item we processed, so we can
  // interleave deletions at the right positions between stable anchors.
  let lastStablePubIdx = -1;

  const emitDeletesBefore = (pubIdx: number) => {
    for (let i = lastStablePubIdx + 1; i < pubIdx; i++) {
      const pub = publishedList[i];
      if (!draftSet.has(pub.list_id)) {
        entries.push({ op: "delete", item: pub });
      }
    }
  };

  // Walk draft order so the result reads as "what the new state looks like".
  // `toIndex` is the item's position in the draft list; `fromIndex` (pubEntry.idx)
  // is its position in the published list. Together they describe a move
  // (e.g. fromIndex 2 → toIndex 0 means "was row 3, now row 1").
  draftList.forEach((draftItem, toIndex) => {
    const pubEntry = publishedMap.get(draftItem.list_id);

    if (!pubEntry) {
      entries.push({ op: "insert", item: draftItem });
    } else if (!stableSet.has(draftItem.list_id)) {
      // Item exists in both but broke relative order → moved.
      const changes = diffItemFields(pubEntry.item, draftItem);
      if (changes.length === 0) {
        entries.push({
          op: "move",
          item: draftItem,
          fromIndex: pubEntry.idx,
          toIndex,
        });
      } else {
        entries.push({
          op: "update",
          published: pubEntry.item,
          draft: draftItem,
          changes,
          moved: true,
          fromIndex: pubEntry.idx,
          toIndex,
        });
      }
    } else {
      // Stable item: interleave any deletions that precede it in published order.
      // LCS keeps stable items monotonic in published order, but the large-list
      // fallback marks every common item stable, so a reordered draft can visit
      // published indexes out of order — never move the cursor backwards, or the
      // final sweep would re-emit deletions already produced.
      emitDeletesBefore(pubEntry.idx);
      lastStablePubIdx = Math.max(lastStablePubIdx, pubEntry.idx);

      const changes = diffItemFields(pubEntry.item, draftItem);
      if (changes.length === 0) {
        entries.push({ op: "equal", item: draftItem });
      } else {
        entries.push({
          op: "update",
          published: pubEntry.item,
          draft: draftItem,
          changes,
          moved: false,
          fromIndex: pubEntry.idx,
          toIndex,
        });
      }
    }
  });

  // Emit any remaining deletions after the last stable item.
  emitDeletesBefore(publishedList.length);

  return { entries, reorderDetectionSkipped };
}

// ─── Public Functions ─────────────────────────────────────────────────────────

export function comparePlaylist<T extends ItemLike>(
  publishedList: T[],
  draftList: T[],
  publishedProps?: unknown,
  draftProps?: unknown,
): ZoneDiff<T> {
  const { entries, reorderDetectionSkipped } = diffList(
    publishedList,
    draftList,
  );
  return {
    entries,
    propsChanges: diffProps(publishedProps, draftProps),
    reorderDetectionSkipped,
  };
}

export function compareChannel(
  published: Content,
  draft: Content,
  publishedLayoutId?: string | null,
  draftLayoutId?: string | null,
): ChannelDiff {
  const layoutChanged = (publishedLayoutId ?? null) !== (draftLayoutId ?? null);

  const draftZoneKeys = new Set(Object.keys(draft.zones));
  const publishedZoneKeys = new Set(Object.keys(published.zones));

  const newZones = Array.from(draftZoneKeys).filter(
    (k) => !publishedZoneKeys.has(k),
  );
  const deletedZones = Array.from(publishedZoneKeys).filter(
    (k) => !draftZoneKeys.has(k),
  );

  const zones: Record<string, ZoneDiff> = {};
  Array.from(draftZoneKeys).forEach((key) => {
    if (publishedZoneKeys.has(key)) {
      zones[key] = comparePlaylist(
        published.zones[key].list,
        draft.zones[key].list,
        published.zones[key].props,
        draft.zones[key].props,
      );
    }
  });

  // Channel-level settings diff (top-level content.props, e.g. default durations).
  const propsChanges = diffProps(published.props, draft.props);

  return { layoutChanged, newZones, deletedZones, propsChanges, zones };
}
